import type { ReactNode } from 'react'
import { Icon } from '@iconify/react'
import { ParticipantThemeStyle } from '@/components/ParticipantThemeStyle'
import {
  buildLandingInstitutionName,
  resolveLandingAccountBackdropUrl,
  resolveLandingAccountPrizeLine,
  resolveLandingAccountTitle,
} from '@/lib/landing-account-visual'
import { getInstitutionBrandImageUrl } from '@/lib/profile-media'
import type { LandingCampaign, LandingInstitution } from '@/types/landing'
import styles from './LandingNumbersPage.module.css'

type LandingProfileShellProps = {
  campaign: LandingCampaign | null
  institution: LandingInstitution | null
  homeHref: string
  onLogout?: () => void
  isLoggingOut?: boolean
  logoutError?: string | null
  /** Rótulo acima do título no hero (ex.: confirmação pós-pagamento). */
  passLabel?: string
  children: ReactNode
}

export function LandingProfileShell({
  campaign,
  institution,
  homeHref,
  onLogout,
  isLoggingOut = false,
  logoutError,
  passLabel = 'Sua área na campanha',
  children,
}: LandingProfileShellProps) {
  const campaignTitle = resolveLandingAccountTitle(campaign)
  const prizeLine = resolveLandingAccountPrizeLine(campaign)
  const backdropUrl = resolveLandingAccountBackdropUrl(campaign)
  const institutionName =
    buildLandingInstitutionName(institution) || campaignTitle || 'Campanha'
  const institutionBrandUrl = getInstitutionBrandImageUrl(institution)

  return (
    <div className={styles.page}>
      <ParticipantThemeStyle campaign={campaign} />

      <div className={styles.shell}>
        <header className={styles.hero} aria-label={campaignTitle || 'Campanha'}>
          {backdropUrl ? (
            <img
              src={backdropUrl}
              alt=""
              className={styles.heroPhoto}
              decoding="async"
              fetchPriority="high"
            />
          ) : (
            <span className={styles.heroMark} aria-hidden>
              {institutionName.slice(0, 1)}
            </span>
          )}
          <div className={styles.heroVeil} />

          <div className={styles.heroInner}>
            <nav className={styles.heroBar} aria-label="Menu da conta">
              <a
                href={homeHref}
                className={styles.heroAction}
                aria-label="Voltar para a campanha"
                title="Voltar para a campanha"
              >
                <Icon icon="mdi:arrow-left" aria-hidden />
                <span className={styles.heroActionText}>Campanha</span>
              </a>
              {onLogout ? (
                <button
                  type="button"
                  className={styles.heroAction}
                  onClick={onLogout}
                  disabled={isLoggingOut}
                  aria-label={isLoggingOut ? 'Saindo da conta' : 'Sair da conta'}
                  title="Sair da conta"
                >
                  <Icon
                    icon={isLoggingOut ? 'mdi:loading' : 'mdi:logout-variant'}
                    aria-hidden
                  />
                  <span className={styles.heroActionText}>
                    {isLoggingOut ? 'Saindo...' : 'Sair'}
                  </span>
                </button>
              ) : null}
            </nav>
            {logoutError ? (
              <p className={styles.heroLogoutError} role="alert">
                {logoutError}
              </p>
            ) : null}

            <div className={styles.heroCopy}>
              <div className={styles.heroIdentityLine}>
                {institutionBrandUrl ? (
                  <img src={institutionBrandUrl} alt="" className={styles.heroLogo} />
                ) : null}
                <span className={styles.heroPassLabel}>
                  <Icon icon="mdi:star-four-points" aria-hidden />
                  {passLabel}
                </span>
              </div>
              <p className={styles.heroKicker}>{institutionName}</p>
              <p className={styles.heroTitle}>{campaignTitle || 'Sua campanha'}</p>
              {prizeLine ? <p className={styles.heroExcerpt}>{prizeLine}</p> : null}
            </div>
          </div>
        </header>

        <main className={styles.main}>{children}</main>
      </div>
    </div>
  )
}
